/**
 * An agent's face: the CaseBlink robot on its hue's tile. Casey is always the
 * blue look. `varied` picks one of six looks (hue and expression) by a hash of
 * the agent's seed, so overlapping faces in a stack tell apart; it stays the
 * same for a seed across sessions and clients.
 */
import { memo, type FC } from "react";

import {
  AgentRobot,
  CASEY_LOOK,
  ROBOT_LOOKS,
  type RobotLook
} from "@/components/brand";
import { cn } from "@/lib/utils";

/** The looks agents are assigned from; kept in the old Moods preset order. */
export const MOOD_PRESETS: readonly RobotLook[] = ROBOT_LOOKS;

/** 32-bit FNV-1a: stable across sessions and platforms. */
/* eslint-disable no-bitwise */
export const seedHash = (seed: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};
/* eslint-enable no-bitwise */

export const moodPresetFor = (seed: string): RobotLook =>
  MOOD_PRESETS[seedHash(seed) % MOOD_PRESETS.length];

export type MoodSeedSource = {
  avatar_seed?: string | null;
  key?: string | null;
  label?: string | null;
  agent_kind?: string | null;
};

/** `avatar_seed`, else the agent's stable key, else its label or kind. */
export const moodSeedOf = (agent: MoodSeedSource): string =>
  agent.avatar_seed || agent.key || agent.label || agent.agent_kind || "agent";

const SIZE = {
  xs: "size-4",
  sm: "size-6",
  md: "size-9",
  lg: "size-10"
} as const;

type Props = {
  agent: MoodSeedSource;
  size?: keyof typeof SIZE;
  /** Blink and bob (the robot's idle loop); off by default. */
  animated?: boolean;
  /** Pick the look from the seed instead of Casey's blue (face stacks, other agents). */
  varied?: boolean;
  className?: string;
};

export const AgentMoodAvatar: FC<Props> = memo(
  ({ agent, size = "sm", animated = false, varied = false, className }) => (
    <AgentRobot
      look={varied ? moodPresetFor(moodSeedOf(agent)) : CASEY_LOOK}
      state={animated ? "idle" : "static"}
      tile
      className={cn(
        SIZE[size],
        "rounded-lg ring-1 ring-line-1",
        size === "xs" && "rounded-xs",
        className
      )}
      data-testid="agent-mood-avatar"
    />
  )
);
AgentMoodAvatar.displayName = "AgentMoodAvatar";
